import React, { useEffect, useMemo, useState } from 'react';
import { FiInfo } from 'react-icons/fi';

import { ModalDefault, Page } from '~/components';
import dashboardService, {
  IEbdDashboardChurchesRankingResponse,
  IEbdDashboardClassesResponse,
  IEbdDashboardLeadersRankingResponse,
  IEbdDashboardSummaryResponse,
  IEbdDashboardTrendAlertsResponse,
} from '~/services/dashboardService';

import {
  AlertCard,
  AlertList,
  Bar,
  BarFill,
  BarHeader,
  Card,
  CardGrid,
  CardLabel,
  CardValue,
  Container,
  Empty,
  Header,
  Period,
  Section,
  SectionGrid,
  SectionTitle,
  SkeletonBar,
  SkeletonCard,
  SkeletonCardGrid,
  SkeletonLine,
  Tooltip,
  TooltipIcon,
} from './styles';

const Home: React.FC = () => {
  const [error, setError] = useState(false);

  const [summary, setSummary] = useState<IEbdDashboardSummaryResponse | null>(
    null,
  );
  const [churchesRanking, setChurchesRanking] =
    useState<IEbdDashboardChurchesRankingResponse | null>(null);
  const [leadersRanking, setLeadersRanking] =
    useState<IEbdDashboardLeadersRankingResponse | null>(null);
  const [classesMetrics, setClassesMetrics] =
    useState<IEbdDashboardClassesResponse | null>(null);
  const [trendAlerts, setTrendAlerts] =
    useState<IEbdDashboardTrendAlertsResponse | null>(null);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingChurches, setLoadingChurches] = useState(true);
  const [loadingLeaders, setLoadingLeaders] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      setLoadingSummary(true);
      try {
        const data = await dashboardService.getSummary();
        setSummary(data);
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoadingSummary(false);
      }
    };

    const loadChurchesRanking = async () => {
      setLoadingChurches(true);
      try {
        const data = await dashboardService.getChurchesRanking();
        setChurchesRanking(data);
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoadingChurches(false);
      }
    };

    const loadLeadersRanking = async () => {
      setLoadingLeaders(true);
      try {
        const data = await dashboardService.getLeadersRanking();
        setLeadersRanking(data);
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoadingLeaders(false);
      }
    };

    const loadClasses = async () => {
      setLoadingClasses(true);
      try {
        const data = await dashboardService.getClassesMetrics();
        setClassesMetrics(data);
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoadingClasses(false);
      }
    };

    const loadTrendAlerts = async () => {
      setLoadingTrend(true);
      try {
        const data = await dashboardService.getTrendAlerts();
        setTrendAlerts(data);
      } catch (e) {
        console.log(e);
        setError(true);
      } finally {
        setLoadingTrend(false);
      }
    };

    loadSummary();
    loadChurchesRanking();
    loadLeadersRanking();
    loadClasses();
    loadTrendAlerts();
  }, []);

  const period =
    summary?.period ||
    churchesRanking?.period ||
    leadersRanking?.period ||
    classesMetrics?.period ||
    trendAlerts?.period ||
    null;

  const maxClassAttendance = useMemo(() => {
    if (!classesMetrics?.classesByAttendance.length) return 0;
    return Math.max(
      ...classesMetrics.classesByAttendance.map(item => item.value),
    );
  }, [classesMetrics]);

  const maxClassCoverage = useMemo(() => {
    if (!classesMetrics?.classesByChurchCoverage.length) return 0;
    return Math.max(
      ...classesMetrics.classesByChurchCoverage.map(item => item.value),
    );
  }, [classesMetrics]);

  const renderSkeletonBars = (count = 6) =>
    Array.from({ length: count }, (_, position) => position + 1).map(item => (
      <SkeletonBar key={`skeleton-${item}`}>
        <SkeletonLine width="72%" />
        <SkeletonLine width="100%" height="8px" />
      </SkeletonBar>
    ));

  return (
    <Page disabledBack>
      {error && (
        <ModalDefault success={false} onClick={() => setError(false)}>
          Algumas informações do dashboard não puderam ser carregadas.
        </ModalDefault>
      )}

      <Container>
        <Header>
          <h1>Dashboard EBD ADPB</h1>
          {!!period && (
            <Period>
              {period.year} - {period.quarter}º trimestre
            </Period>
          )}
        </Header>

        {loadingSummary && (
          <SkeletonCardGrid>
            {[1, 2, 3, 4, 5, 6].map(item => (
              <SkeletonCard key={`summary-skeleton-${item}`} />
            ))}
          </SkeletonCardGrid>
        )}

        {!loadingSummary && !!summary && (
          <CardGrid>
            <Card>
              <CardLabel>
                Congregações cadastradas
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Total de congregações da ADPB cadastradas no sistema.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>{summary.summary.totalChurches}</CardValue>
            </Card>
            <Card>
              <CardLabel>
                Registros no trimestre
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Quantidade total de registros de frequência lançados no
                    trimestre atual.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>{summary.summary.totalRegisters}</CardValue>
            </Card>
            <Card>
              <CardLabel>
                Cobertura de congregações
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Percentual de congregações que já registraram frequência no
                    trimestre atual.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>
                {summary.summary.churchesCoveragePercentage}%
              </CardValue>
            </Card>
            <Card>
              <CardLabel>
                Média geral de frequência
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Média percentual de presença em todos os registros do
                    trimestre.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>
                {summary.summary.averagePresencePercentage}%
              </CardValue>
            </Card>
            <Card>
              <CardLabel>
                Engajamento dos dirigentes
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Percentual de dirigentes ativos com ao menos um registro de
                    frequência no trimestre.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>
                {summary.summary.leadersEngagementPercentage}%
              </CardValue>
            </Card>
            <Card>
              <CardLabel>
                Média de classes por congregação
                <Tooltip>
                  <TooltipIcon>
                    <FiInfo />
                  </TooltipIcon>
                  <span>
                    Média de classes vinculadas por congregação no cadastro
                    atual.
                  </span>
                </Tooltip>
              </CardLabel>
              <CardValue>{summary.summary.averageClassesPerChurch}</CardValue>
            </Card>
          </CardGrid>
        )}

        <SectionGrid>
          <Section>
            <SectionTitle>
              Congregações com maior frequência percentual
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Ranking das congregações com maior média percentual de
                  presença nas frequências registradas no trimestre atual.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingChurches && renderSkeletonBars(7)}
            {!loadingChurches && !churchesRanking?.highest.length && (
              <Empty>Sem registros no trimestre atual.</Empty>
            )}
            {!loadingChurches &&
              churchesRanking?.highest.map(item => (
                <Bar key={`church-high-${item.churchId}`}>
                  <BarHeader>
                    <span>{item.church}</span>
                    <b>{item.averagePresence}%</b>
                  </BarHeader>
                  <BarFill>
                    <div
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(item.averagePresence, 100),
                        )}%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>

          <Section>
            <SectionTitle>
              Alerta: congregações com menor frequência percentual
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Ranking das congregações com menor média percentual de
                  presença no trimestre atual.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingChurches && renderSkeletonBars(7)}
            {!loadingChurches && !churchesRanking?.lowest.length && (
              <Empty>Sem registros no trimestre atual.</Empty>
            )}
            {!loadingChurches &&
              churchesRanking?.lowest.map(item => (
                <Bar key={`church-low-${item.churchId}`}>
                  <BarHeader>
                    <span>{item.church}</span>
                    <b>{item.averagePresence}%</b>
                  </BarHeader>
                  <BarFill danger>
                    <div
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(item.averagePresence, 100),
                        )}%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>
        </SectionGrid>

        <SectionGrid>
          <Section>
            <SectionTitle>
              Assiduidade dos dirigentes (maiores)
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Ranking histórico de assiduidade dos dirigentes, calculado por
                  domingos com registro enviado em relação aos domingos
                  esperados durante os períodos em que atuaram nas congregações.
                  O nome da congregação atual aparece entre parênteses.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingLeaders && renderSkeletonBars(7)}
            {!loadingLeaders && !leadersRanking?.highest.length && (
              <Empty>Sem dados históricos de dirigentes.</Empty>
            )}
            {!loadingLeaders &&
              leadersRanking?.highest.map(item => (
                <Bar key={`leader-high-${item.leaderId}`}>
                  <BarHeader>
                    <span>
                      {item.leader} ({item.currentChurch})
                    </span>
                    <b>{item.averagePresence}%</b>
                  </BarHeader>
                  <BarFill>
                    <div
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(item.averagePresence, 100),
                        )}%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>

          <Section>
            <SectionTitle>
              Alerta: assiduidade dos dirigentes (menores)
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Ranking histórico com os menores índices de assiduidade dos
                  dirigentes, considerando todos os períodos de atuação.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingLeaders && renderSkeletonBars(7)}
            {!loadingLeaders && !leadersRanking?.lowest.length && (
              <Empty>Sem dados históricos de dirigentes.</Empty>
            )}
            {!loadingLeaders &&
              leadersRanking?.lowest.map(item => (
                <Bar key={`leader-low-${item.leaderId}`}>
                  <BarHeader>
                    <span>
                      {item.leader} ({item.currentChurch})
                    </span>
                    <b>{item.averagePresence}%</b>
                  </BarHeader>
                  <BarFill danger>
                    <div
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(item.averagePresence, 100),
                        )}%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>
        </SectionGrid>

        <SectionGrid>
          <Section>
            <SectionTitle>
              Distribuição por classes (presenças)
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Soma de presenças por classe considerando os registros do
                  trimestre atual.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingClasses && renderSkeletonBars(7)}
            {!loadingClasses && !classesMetrics?.classesByAttendance.length && (
              <Empty>Sem presenças por classe no trimestre atual.</Empty>
            )}
            {!loadingClasses &&
              classesMetrics?.classesByAttendance.map(item => (
                <Bar key={`class-attendance-${item.name}`}>
                  <BarHeader>
                    <span>{item.name}</span>
                    <b>{item.value}</b>
                  </BarHeader>
                  <BarFill>
                    <div
                      style={{
                        width: `${
                          maxClassAttendance > 0
                            ? (item.value / maxClassAttendance) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>

          <Section>
            <SectionTitle>
              Cobertura de classes por congregações
              <Tooltip>
                <TooltipIcon>
                  <FiInfo />
                </TooltipIcon>
                <span>
                  Quantidade de congregações vinculadas a cada classe
                  atualmente.
                </span>
              </Tooltip>
            </SectionTitle>
            {loadingClasses && renderSkeletonBars(7)}
            {!loadingClasses &&
              !classesMetrics?.classesByChurchCoverage.length && (
                <Empty>Sem classes vinculadas às congregações.</Empty>
              )}
            {!loadingClasses &&
              classesMetrics?.classesByChurchCoverage.map(item => (
                <Bar key={`class-coverage-${item.name}`}>
                  <BarHeader>
                    <span>{item.name}</span>
                    <b>{item.value}</b>
                  </BarHeader>
                  <BarFill>
                    <div
                      style={{
                        width: `${
                          maxClassCoverage > 0
                            ? (item.value / maxClassCoverage) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </BarFill>
                </Bar>
              ))}
          </Section>
        </SectionGrid>

        <Section>
          <SectionTitle>
            Tendência por lição no trimestre
            <Tooltip>
              <TooltipIcon>
                <FiInfo />
              </TooltipIcon>
              <span>
                Média percentual de presença por lição registrada no trimestre
                atual.
              </span>
            </Tooltip>
          </SectionTitle>
          {loadingTrend && renderSkeletonBars(7)}
          {!loadingTrend && !trendAlerts?.lessonTrend.length && (
            <Empty>Sem tendência para exibir no trimestre atual.</Empty>
          )}
          {!loadingTrend &&
            trendAlerts?.lessonTrend.map(item => (
              <Bar key={`lesson-${item.lesson}`}>
                <BarHeader>
                  <span>Lição {item.lesson}</span>
                  <b>{item.averagePresence}%</b>
                </BarHeader>
                <BarFill>
                  <div
                    style={{
                      width: `${Math.max(
                        0,
                        Math.min(item.averagePresence, 100),
                      )}%`,
                    }}
                  />
                </BarFill>
              </Bar>
            ))}
        </Section>

        <SectionGrid>
          <Section>
            <SectionTitle>Tendências e alertas</SectionTitle>
            {loadingTrend && renderSkeletonBars(4)}
            {!loadingTrend && (
              <AlertList>
                {trendAlerts?.alerts.map(alert => (
                  <AlertCard key={alert.title} type={alert.type}>
                    <b>{alert.title}</b>
                    <p>{alert.description}</p>
                  </AlertCard>
                ))}
              </AlertList>
            )}
          </Section>

          <Section>
            <SectionTitle>Congregações sem registro no trimestre</SectionTitle>
            {loadingTrend && renderSkeletonBars(4)}
            {!loadingTrend && !trendAlerts?.churchesWithoutRegister.length && (
              <Empty>Todas as congregações registraram frequência.</Empty>
            )}
            {!loadingTrend && !!trendAlerts?.churchesWithoutRegister.length && (
              <AlertList>
                {trendAlerts?.churchesWithoutRegister.map(church => (
                  <AlertCard key={church} type="warning">
                    <b>{church}</b>
                  </AlertCard>
                ))}
              </AlertList>
            )}
          </Section>
        </SectionGrid>
      </Container>
    </Page>
  );
};

export default Home;
